import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useCurrentUser } from '../../lib/currentUser';
import { useStudents } from 'context/StudentsContext';

// Import simplified dashboard components
import QuickNavigation from './components/QuickNavigation';
import RecentStudents from './components/RecentStudents';
import ActivityFeed from './components/ActivityFeed';
import SummaryWidgets from './components/SummaryWidgets';
import StudentSearchBar from './components/StudentSearchBar';
import AddStudentModal from './components/AddStudentModal';
import CollectDataLauncher from '../../components/CollectDataLauncher';
import GetStartedGuide from './components/GetStartedGuide';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [isCollectDataOpen, setIsCollectDataOpen] = useState(false);
  const currentUser = useCurrentUser();
  const { students } = useStudents();

  // Empty-roster mode: the workspace has zero students. We collapse
  // the dashboard down to just the Welcome + Get Started guide so a
  // first-time user isn't looking at four empty widgets ("0 students",
  // "0 goals", empty activity feed) trying to figure out what to do
  // first. Same shape as Gmail's empty inbox or Notion's blank
  // workspace: get the user to the ONE useful action.
  const isEmptyRoster = (students || []).length === 0;

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'add-student':
        setIsAddStudentOpen(true);
        break;
      case 'collect-data':
        setIsCollectDataOpen(true);
        break;
      case 'log-incident':
        navigate('/behavioral-incident-logging');
        break;
      case 'update-goal':
        navigate('/iep-goals-and-objectives-tracking');
        break;
      case 'generate-report':
        navigate('/reports-and-analytics');
        break;
      default:
        break;
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header 
        onMenuToggle={handleSidebarToggle}
        isMenuOpen={isSidebarOpen}
      />
      
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleSidebarCollapse}
      />
      
      {/* Main Content */}
      <main className={`transition-all duration-300 pt-16 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
      }`}>
        <div className="p-6 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb />

          {/* Simplified Welcome Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                  {currentUser?.firstName
                    ? `Welcome back, ${currentUser.firstName}!`
                    : 'Welcome back!'}
                </h1>
                {/* Local clock only — a teacher glancing at their phone
                    doesn't need "System Online" reassurance and it looked
                    like an alert. If the app is offline they'll see the
                    yellow demo banner or the amber setup banner instead. */}
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Icon name="Clock" size={14} />
                  <span>{formatTime(currentTime)}</span>
                </div>
              </div>
              
              {/* Primary CTAs. "Collect Data" is the hero — that's the
                  action a teacher runs 20 times a day. "Add Student" is
                  the once-a-year setup action and drops to secondary.
                  On an empty roster we hide both — the Get Started
                  guide right below will surface the single correct
                  next action ("Add your first student") so the user
                  isn't looking at two same-color buttons and picking
                  wrong. */}
              {!isEmptyRoster && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => handleQuickAction('collect-data')}
                    iconName="ClipboardEdit"
                    iconPosition="left"
                    iconSize={18}
                    className="sm:px-6 shadow-sm"
                  >
                    Collect Data
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleQuickAction('add-student')}
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Add Student
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* First-time / nearly-empty workspaces: a big plain-English
              "here's what to do next" panel. Auto-hides once the teacher
              has logged their first incident or goal data point. */}
          <GetStartedGuide
            onAddStudentClick={() => handleQuickAction('add-student')}
            onCollectDataClick={() => handleQuickAction('collect-data')}
          />

          {/* Empty-roster mode: hide search, activity, summary numbers,
              and recent students. All four are empty widgets that just
              add visual noise to a first-time dashboard. We show them
              back the moment the user adds their first student. */}
          {!isEmptyRoster && (
            <>
              <StudentSearchBar />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                <div className="lg:col-span-1">
                  <QuickNavigation />
                </div>
                <div className="lg:col-span-2">
                  <ActivityFeed />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <SummaryWidgets />
                <RecentStudents />
              </div>
            </>
          )}

          {/* On an empty roster, still show the quick-nav tiles below
              the get-started guide — they're the only way to reach the
              IEP Goals, Behavior, and Reports pages from the dashboard,
              and the tiles are labeled + iconified enough to work as
              standalone nav for someone browsing what the app does. */}
          {isEmptyRoster && (
            <div className="mt-2">
              <QuickNavigation />
            </div>
          )}

          {/* Mobile Quick Actions. Floating "Collect Data" is the hero
              on small screens — it's what a para reaches for in the
              hallway. Hidden on an empty roster because there's nothing
              to collect yet and the button would just look like a
              mystery blue pill overlapping the Get Started guide. */}
          {!isEmptyRoster && (
            <div className="fixed bottom-6 right-6 lg:hidden">
              <Button
                variant="default"
                onClick={() => handleQuickAction('collect-data')}
                className="rounded-full shadow-lg px-5 h-14"
                iconName="ClipboardEdit"
                iconPosition="left"
                iconSize={20}
              >
                Collect Data
              </Button>
            </div>
          )}
        </div>
      </main>

      <AddStudentModal
        isOpen={isAddStudentOpen}
        onClose={() => setIsAddStudentOpen(false)}
        onCreated={(student) => {
          if (student?.id) {
            navigate(
              `/student-profile-management/${encodeURIComponent(student.id)}`,
            );
          }
        }}
      />

      <CollectDataLauncher
        isOpen={isCollectDataOpen}
        onClose={() => setIsCollectDataOpen(false)}
      />
    </div>
  );
};

export default Dashboard;